import React from 'react';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import UpdateIcon from '@material-ui/icons/Update';
import { useEntity, useEntityOwnership } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { isAdminRole } from '../../utils/roles';
import { ManagedComponentsResolver } from '../../utils/managedComponents';

interface UpdateComponentButtonProps {
  entity?: any;
  size?: 'small' | 'medium';
  render?: 'icon' | 'button';
  fullWidth?: boolean;
  label?: string;
}

const UpdateComponentButtonWithContext = ({ size = 'small', render = 'icon', fullWidth, label }: Omit<UpdateComponentButtonProps, 'entity'>) => {
  const { entity } = useEntity();
  return <UpdateComponentButtonInternal entity={entity} size={size} render={render} fullWidth={fullWidth} label={label} />;
};

const UpdateComponentButtonInternal = ({ entity, size = 'small', render = 'icon', fullWidth, label }: Required<Pick<UpdateComponentButtonProps, 'entity'>> & Omit<UpdateComponentButtonProps, 'entity'>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const { isOwnedEntity } = useEntityOwnership();
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [isExecuting, setIsExecuting] = React.useState<boolean>(false);

  // The Update action triggers a Git-source rebuild via `source_type: git`.
  // Whether it applies is resolved from the type's `supportsUpdate` flag in
  // app-config AND from the entity itself -- see the resolver. Both matter: the
  // MySQL module opts out at the type level, while a database-only component in
  // a tier that otherwise builds images is excluded by having no image.
  const managed = React.useMemo(
    () => new ManagedComponentsResolver(configApi),
    [configApi],
  );
  const detectedType = React.useMemo(
    () => managed.detectType(entity),
    [managed, entity],
  );
  const supportsUpdate = React.useMemo(
    () => managed.supportsUpdate(entity),
    [managed, entity],
  );
  const updateTemplateRef = detectedType?.templates.primary;

  // Owner no longer used for UI update permissions; keep for future if needed

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs ?? [];
        const admin = isAdminRole(ownershipEntityRefs, configApi);
        if (mounted) setIsAdmin(admin);
      } catch (_e) {
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  // determine below after computing ownership

  const handleNavigate = async () => {
    if (!entity) return;
    try {
      setIsExecuting(true);
      const fullEntityName = entity.metadata.name as string;
      const { baseName, targetUser } = managed.parseEntityName(entity);

      const namespace = entity.metadata.annotations?.['kubernetes.io/namespace'] as string | undefined;
      if (!namespace) {
        alert('Cannot update: Kubernetes namespace not found in entity annotations.');
        return;
      }

      const confirmed = window.confirm(
        `This will use your Git source to build a new image and update "${fullEntityName}". Open the corresponding template for rollback options.\n\nProceed?`
      );
      if (!confirmed) {
        return;
      }

      if (!updateTemplateRef) return; // Should be unreachable due to button gating below
      const values: Record<string, any> = {
        deployment_intent: 'update',
        source_type: 'git',
        version_bump: 'patch',
        name: baseName,
        namespace: namespace,
        expirationDays: 365,
      };

      // This button renders for `isAdmin || isOwner`, so the entity on screen
      // is not necessarily the caller's. Without targetUser, resolveTarget
      // falls back to the caller and the template patches
      // django-webserver-<name>-<admin> instead of the app being viewed --
      // which either fails outright or, if the admin happens to own an app of
      // the same base name, quietly updates theirs with this app's image tag.
      // resolveTarget authorizes the value server-side: a no-op when the
      // caller owns the entity, admins-only otherwise.
      if (targetUser) {
        values.targetUser = targetUser;
      }

      const response = await scaffolderApi.scaffold({
        templateRef: updateTemplateRef,
        values,
      });

      if (response.taskId) {
        window.location.href = `/create/tasks/${response.taskId}`;
      }
    } catch (e) {
      console.error('Failed to trigger update template:', e);
      alert('Failed to trigger update. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  const isOwner = React.useMemo(() => {
    try {
      return !!(entity && isOwnedEntity(entity));
    } catch {
      return false;
    }
  }, [entity, isOwnedEntity]);

  // Note: Removed localStorage-based deletion tracking
  // If resources are deleted, Update will fail naturally and student sees the error
  // They can then use the template to create fresh resources

  const canUpdate = isAdmin || isOwner;

  if (!entity || !supportsUpdate || !updateTemplateRef || !canUpdate) {
    return null;
  }

  if (render === 'button') {
    return (
      <Tooltip title={isExecuting ? 'Updating...' : 'Update Application'}>
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNavigate}
            disabled={isExecuting}
            fullWidth={!!fullWidth}
          >
            {label || 'Update'}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isExecuting ? 'Updating...' : 'Update Application'}>
      <span>
        <IconButton color="primary" onClick={handleNavigate} size={size} disabled={isExecuting}>
          <UpdateIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const UpdateComponentButton = ({ entity: propEntity, size = 'small', render = 'icon', fullWidth, label }: UpdateComponentButtonProps) => {
  if (propEntity) {
    return <UpdateComponentButtonInternal entity={propEntity} size={size} render={render} fullWidth={fullWidth} label={label} />;
  }
  return <UpdateComponentButtonWithContext size={size} render={render} fullWidth={fullWidth} label={label} />;
};


